<template>
    <nav class="flex items-center justify-between bg-white h-16 px-5 shadow-sm fixed top-0 left-0 right-0 z-[1000]">
        <!-- Logo et bienvenue -->
        <div class="flex items-center gap-5">
            <router-link to="/dashboard">
                <img src="../assets/images/sellhub-logo.png" alt="Logo" class="h-8 w-auto">
            </router-link>

            <div class="flex flex-col items-start gap-0.5">
                <span class="text-[0.8rem] text-gray-600">Bienvenue, 👋</span>
                <span class="text-[0.95rem] font-semibold text-primary">{{ userName }}</span>
            </div>
        </div>

        <!-- Barre de recherche -->
        <div class="flex items-center bg-gray-50 rounded-3xl px-5 py-2.5 flex-1 mx-8 max-w-[500px]">
            <BaseInput
                v-model="searchQuery"
                type="search"
                placeholder="Rechercher un produit..."
                @keyup.enter="handleSearch"
                class="flex-1 border-0 bg-transparent focus:ring-0 p-0"
            >
                <template #icon-left>
                    <BaseIcon name="search" size="18" color="#94a3b8" />
                </template>
            </BaseInput>
            <button
                @click="handleSearch"
                class="ml-2 p-1.5 bg-primary hover:bg-primary-dark text-white rounded-full transition-colors"
            >
                <BaseIcon name="search" size="16" color="white" />
            </button>
        </div>

        <!-- Actions utilisateur -->
        <div class="flex items-center gap-5">
            <!-- Notifications -->
            <div class="relative">
                <NotificationPanel />
            </div>

            <!-- Menu utilisateur -->
            <div class="relative">
                <div
                    @click="toggleUserMenu"
                    class="flex items-center gap-2.5 cursor-pointer px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <!-- Avatar -->
                    <div class="relative">
                        <img
                            v-if="userAvatar"
                            :src="userAvatar"
                            alt="Avatar"
                            class="w-9 h-9 rounded-full object-cover border-2 border-primary/20"
                        />
                        <div
                            v-else
                            class="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm"
                        >
                            {{ userInitials }}
                        </div>
                        <div class="absolute bottom-0 right-0 w-2.5 h-2.5 bg-secondary border-2 border-white rounded-full"></div>
                    </div>

                    <span class="text-sm font-medium text-gray-700">{{ userName }}</span>
                    <BaseIcon
                        name="chevron-down"
                        size="16"
                        :class="['transition-transform', showUserMenu ? 'rotate-180' : '']"
                    />
                </div>

                <!-- Dropdown menu -->
                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <div
                        v-if="showUserMenu"
                        v-click-outside="closeUserMenu"
                        class="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
                    >
                        <router-link
                            to="/profile"
                            class="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors no-underline"
                            @click="showUserMenu = false"
                        >
                            <BaseIcon name="user" size="16" />
                            <span>Mon profil</span>
                        </router-link>

                        <router-link
                            to="/settings"
                            class="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors no-underline"
                            @click="showUserMenu = false"
                        >
                            <BaseIcon name="cog" size="16" />
                            <span>Paramètres</span>
                        </router-link>

                        <div class="h-px bg-gray-200 my-1"></div>

                        <button
                            @click="showLogoutModal = true"
                            class="w-full flex items-center gap-3 px-4 py-3 text-sm text-danger hover:bg-red-50 transition-colors border-0 bg-transparent cursor-pointer text-left"
                        >
                            <BaseIcon name="arrow-right" size="16" />
                            <span>Se déconnecter</span>
                        </button>
                    </div>
                </transition>
            </div>
        </div>
    </nav>

    <!-- Modal de confirmation de déconnexion -->
    <BaseModal
        v-model="showLogoutModal"
        title="Confirmation de déconnexion"
        size="sm"
        show-cancel-button
        show-confirm-button
        cancel-text="Annuler"
        confirm-text="Se déconnecter"
        confirm-variant="danger"
        :confirm-loading="logoutLoading"
        @confirm="confirmLogout"
        @cancel="showLogoutModal = false"
    >
        <div class="text-center py-4">
            <div class="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BaseIcon name="arrow-right" size="32" color="#dc3545" />
            </div>
            <p class="text-gray-600">Êtes-vous sûr de vouloir vous déconnecter ?</p>
        </div>
    </BaseModal>
</template>

<script>
import { authService } from '@/services';
import NotificationPanel from '@/views/notifications/NotificationPanel.vue';
import CashRegisterService from '@/services/cashRegisterService';
import { useAuthStore } from '@/store/modules/auth';

export default {
    name: 'NavBar',
    components: {
        NotificationPanel
    },
    directives: {
        clickOutside: {
            mounted(el, binding) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    },
    data() {
        return {
            searchQuery: '',
            showUserMenu: false,
            userName: '',
            userAvatar: null,
            showLogoutModal: false,
            logoutLoading: false,
            cashRegister: null
        }
    },
    computed: {
        userInitials() {
            if (!this.userName) return '?';
            return this.userName
                .split(' ')
                .map(name => name.charAt(0))
                .join('')
                .toUpperCase();
        }
    },
    async created() {
        try {
            if (CashRegisterService.hasOpenedCashRegister()) {
                const cashRegister = CashRegisterService.getOpenedCashRegister();
                this.cashRegister = cashRegister;
            }
            const userInfo = await authService.getUser();
            this.userName = userInfo.name || 'Utilisateur';
        } catch (error) {
            console.error('Erreur lors de la récupération des informations utilisateur:', error);
        }
    },
    methods: {
        async confirmLogout() {
            this.logoutLoading = true;
            try {
                await useAuthStore().logout();
                this.showLogoutModal = false;
                this.showUserMenu = false;

                this.$nextTick(() => {
                    this.$router.push('/login');
                    this.logoutLoading = false;
                });
            } catch (error) {
                console.error('Erreur lors de la déconnexion:', error);
                this.logoutLoading = false;
                this.$router.push('/login');
            }
        },
        handleSearch() {
            if (this.searchQuery.trim()) {
                this.$router.push({
                    path: '/products/search',
                    query: { q: this.searchQuery }
                });
            }
        },
        toggleUserMenu() {
            this.showUserMenu = !this.showUserMenu;
        },
        closeUserMenu() {
            this.showUserMenu = false;
        }
    }
}
</script>

<style scoped>
/* Minimal scoped styles - most styling is done with Tailwind */
</style>
